import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  type CreateActionRewardConfigRequestBody,
  type CreateActionRewardConfigResponse,
  type UpdateActionRewardConfigRequestBody,
  type UpdateActionRewardConfigResponse,
  createActionRewardConfig,
  updateActionRewardConfig,
} from '~/services/LuxLoyalty';

import { actionRewardConfigQueryKeys } from './actionRewardConfigQueryKeys';

interface MutationOptions<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
}

/** Create an action reward config. */
export function useCreateActionRewardConfig(options: MutationOptions<CreateActionRewardConfigResponse> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateActionRewardConfigRequestBody) => createActionRewardConfig(body),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: actionRewardConfigQueryKeys.list() });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
}

/** Update an action reward config by id. */
export function useUpdateActionRewardConfig(options: MutationOptions<UpdateActionRewardConfigResponse> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateActionRewardConfigRequestBody }) =>
      updateActionRewardConfig(id, body),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: actionRewardConfigQueryKeys.list() });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
}
